import React from "react";
import usePulseLocale from "./i18n";
import { tokens, fmtNum, fmtCompact, fmtPct, fmtPeriod } from "./theme";
import { regionName } from "./regionNames";

const marketName = (x, lang, t) => {
  if (x.code === "CH_LI") return t("vuln_market_ch_li");
  if (!x.iso2) return x.label;
  try { return new Intl.DisplayNames([lang], { type: "region" }).of(x.iso2) || x.label; } catch { return x.label; }
};

const COMPONENTS = ["pressure", "seasonality", "concentration", "air", "platform"];

const Bar = ({ v, dm, strong }) => {
  const T = tokens(dm);
  if (v === null || v === undefined) return <span style={{ color: T.muted }}>—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ width: 44, height: 6, background: T.surface2, display: "inline-block", position: "relative" }} aria-hidden="true">
        <span style={{ width: `${v}%`, height: 6, background: strong ? T.brand : T.accent, display: "block" }} />
      </span>
      <span className="pulse-num" style={{ minWidth: 30, textAlign: "right" }}>{v.toFixed(0)}</span>
    </span>
  );
};

/** Raw values behind one region's scores (what the min-max normalisation is based on). */
const Detail = ({ r, dm, lang, t }) => {
  const T = tokens(dm);
  const c = r.components;
  const d = (k) => c[k]?.detail || {};
  const p = d("pressure"), s = d("seasonality"), o = d("concentration"), a = d("air"), pl = d("platform");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 px-3 py-3 text-[11px]" style={{ background: T.surface2 }}
      data-testid={`vuln-detail-${r.code}`}>
      <div><b>{t("vuln_pressure")}</b> — {fmtNum(p.per_1000_pop, lang, 0)} {t("vuln_per_1000")} · {fmtNum(p.per_km2, lang, 0)} {t("vuln_per_km2")}
        <span style={{ color: T.muted }}> ({fmtCompact(p.nights, lang)} {t("vuln_nights")} {p.year}; pop {p.population_year}, km² {p.area_year})</span></div>
      <div><b>{t("vuln_seasonality")}</b> — Gini {fmtNum(s.gini, lang, 2)} · {t("vuln_peak")} {fmtPct(s.peak_month_share_pct, lang, false)} <span style={{ color: T.muted }}>({s.year})</span></div>
      <div><b>{t("vuln_concentration")}</b> — {o.hhi !== undefined ? <>HHI {fmtNum(o.hhi, lang, 0)} · top-3 {fmtPct(o.top3_share_pct, lang, false)}
        <span style={{ color: T.muted }}> ({(o.top3 || []).map((x) => `${marketName(x, lang, t)} ${x.share_pct}%`).join(", ")}; {o.year})</span></> : <span style={{ color: T.muted }}>{t("no_data")}</span>}</div>
      <div><b>{t("vuln_air")}</b> — {a.per_100_nights !== undefined ? <>{fmtNum(a.per_100_nights, lang, 1)} {t("vuln_pax_per_100")}
        <span style={{ color: T.muted }}> ({a.airports?.length ? a.airports.join(", ") : t("vuln_no_airport")}{a.from ? `; ${fmtPeriod(a.from, lang)} – ${fmtPeriod(a.to, lang)}` : ""})</span></> : <span style={{ color: T.muted }}>{t("no_data")}</span>}</div>
      <div><b>{t("vuln_platform")}</b> — {pl.share_pct !== undefined ? <>{fmtPct(pl.share_pct, lang, false)} {t("vuln_platform_share")}
        <span style={{ color: T.muted }}> ({fmtPeriod(pl.from, lang)} – {fmtPeriod(pl.to, lang)})</span></> : <span style={{ color: T.muted }}>{t("no_data")}</span>}</div>
    </div>
  );
};

export default function VulnerabilityTable({ data, dm, selected, onSelect, metric }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const rows = [...data.regions].sort((a, b) => (b.composite ?? -1) - (a.composite ?? -1));
  return (
    <div className="overflow-x-auto" style={{ border: `1px solid ${T.border}` }} data-testid="vuln-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: T.muted, borderBottom: `2px solid ${T.border}` }}>
            <th className="px-2 py-2 font-bold" scope="col">#</th>
            <th className="px-2 py-2 font-bold" scope="col">{t("vuln_col_region")}</th>
            <th className="px-2 py-2 font-bold" scope="col" style={{ color: metric === "composite" ? T.text : T.muted }}>{t("vuln_composite")}</th>
            {COMPONENTS.map((c) => (
              <th key={c} className="px-2 py-2 font-bold whitespace-nowrap" scope="col" style={{ color: metric === c ? T.text : T.muted }}>
                {t(`vuln_${c}_short`)} <span className="pulse-num font-normal">{data.methodology.weights[c]}%</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <React.Fragment key={r.code}>
              <tr onClick={() => onSelect(r.code)} data-testid={`vuln-row-${r.code}`} className="cursor-pointer"
                style={{ borderTop: `1px solid ${T.border}`, background: selected === r.code ? T.surface2 : "transparent" }}
                aria-expanded={selected === r.code}>
                <td className="px-2 py-1.5 pulse-num" style={{ color: T.muted }}>{r.rank ?? "—"}</td>
                <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{regionName(r.code, lang, r.label)}
                  {r.components_available < 5 && <span className="ml-1 text-[9px]" style={{ color: T.muted }} title={t("vuln_partial")}>({r.components_available}/5)</span>}</td>
                <td className="px-2 py-1.5"><Bar v={r.composite} dm={dm} strong /></td>
                {COMPONENTS.map((c) => <td key={c} className="px-2 py-1.5"><Bar v={r.components[c]?.score} dm={dm} /></td>)}
              </tr>
              {selected === r.code && (
                <tr><td colSpan={3 + COMPONENTS.length} className="p-0"><Detail r={r} dm={dm} lang={lang} t={t} /></td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
